import React, { useState, useEffect } from 'react';
import { 
  Code, Users, TrendingUp, Shield, ArrowRight, Play, 
  CheckCircle, Zap, DollarSign, Target, GitBranch, 
  Trophy, Star, Clock, Lightbulb, HandHeart, Coins
} from 'lucide-react';

import { useNavigate } from 'react-router-dom';

const CelystikActionGuide = () => {
  const [activeRole, setActiveRole] = useState('creators');
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const navigate = useNavigate();

  const roleData = {
    creators: {
      title: "Innovation Creators",
      tagline: "Build without upfront costs, own your success. Create projects with customized tokenomics, define milestones and tasks, and drive innovation through decentralized collaboration.",
    
      icon: <Lightbulb className="w-8 h-8" />,
      color: "from-purple-500 to-indigo-600",
      bgGradient: "from-purple-50 via-indigo-50 to-purple-50",
      shadowColor: "shadow-purple-500/20",
      problem: "Need resources but can't afford to pay contributors upfront?",
      solution: "Create projects, get work done through tokenized collaboration",
      actions: [
        {
          step: "Create Project",
          description: "Upload your code, paper, dataset, or idea",
          detail: "Gets tokenized as NFT with immutable attribution",
          icon: <Code className="w-5 h-5" />
        },
        {
          step: "Post Tasks",
          description: "Break down work into tasks for contributors",
          detail: "Contributors become fractional owners, not just workers",
          icon: <Target className="w-5 h-5" />
        },
        {
          step: "Collaborate & Grow",
          description: "Manage via decentralized task boards",
          detail: "Contributors are incentivized for honest work (they own success)",
          icon: <GitBranch className="w-5 h-5" />
        },
        {
          step: "Scale with Success",
          description: "As project grows, everyone benefits automatically",
          detail: "POI rewards flow to all IU holders based on real impact",
          icon: <Trophy className="w-5 h-5" />
        }
      ],
      benefits: [
        "No upfront payment needed",
        "Contributors are long-term aligned",
        "Automatic scaling with impact",
        "Retain ownership while growing"
      ],
      cta: "Create Your Project",
      ctaColor: "bg-gradient-to-r from-purple-600 to-indigo-600"
    },
    contributors: {
      title: "Skilled Contributors",
      tagline: "Stop freelancing, start owning your work's success. Build and grow projects by completing tasks, earning ownership (IUs), and shaping the future of innovation.",
      icon: <Users className="w-8 h-8" />,
      color: "from-green-500 to-emerald-600",
      bgGradient: "from-green-50 via-emerald-50 to-green-50",
      shadowColor: "shadow-green-500/20",
      problem: "Tired of one-time payments with no upside from project success?",
      solution: "Contribute to projects and become a fractional owner",
      actions: [
        {
          step: "Browse Projects",
          description: "Find projects matching your skills and interests",
          detail: "Filter by technology, domain, and potential impact",
          icon: <Target className="w-5 h-5" />
        },
        {
          step: "Complete Tasks",
          description: "Apply for tasks or submit to open challenges",
          detail: "Earn Innovation Units (IUs) for quality contributions",
          icon: <CheckCircle className="w-5 h-5" />
        },
        {
          step: "Become Owner",
          description: "IUs give you fractional ownership of the project",
          detail: "Success/failure directly affects your long-term rewards",
          icon: <Star className="w-5 h-5" />
        },
        {
          step: "Earn Forever",
          description: "Get ongoing rewards as project gains impact",
          detail: "POI emissions automatically distributed to IU holders",
          icon: <Zap className="w-5 h-5" />
        }
      ],
      benefits: [
        "Long-term earning potential",
        "Ownership in successful projects",
        "Aligned incentives for quality",
        "Automatic reward distribution"
      ],
      cta: "Find Tasks",
      ctaColor: "bg-gradient-to-r from-green-600 to-emerald-600"
    },
    investors: {
      title: "Smart Investors",
      tagline: "Fund innovation early, earn from breakthrough success. Fund early innovation by investing capital and sharing in project success through Innovation Units ownership.",
      icon: <TrendingUp className="w-8 h-8" />,
      color: "from-blue-500 to-cyan-600",
      bgGradient: "from-blue-50 via-cyan-50 to-blue-50",
      shadowColor: "shadow-blue-500/20",
      problem: "Want to invest in early innovation but limited access to deals?",
      solution: "Buy IUs of promising projects and provide crucial liquidity",
      actions: [
        {
          step: "Start Investing",
          description: "Discover early-stage innovations across domains",
          detail: "Access to projects before traditional funding rounds",
          icon: <Target className="w-5 h-5" />
          
        },
        {
          step: "Buy IUs",
          description: "Purchase Innovation Units for fractional ownership",
          detail: "Your investment goes directly to project treasury",
          icon: <DollarSign className="w-5 h-5" />
        },
        {
          step: "Provide Liquidity",
          description: "Enable creators/contributors to realize value",
          detail: "IUs become liquid assets backed by project treasury",
          icon: <Coins className="w-5 h-5" />
        },
        {
          step: "Earn from Impact",
          description: "Benefit as projects gain real-world adoption",
          detail: "Higher POI scores = higher CEL emissions to IU holders",
          icon: <Trophy className="w-5 h-5" />
        }
      ],
      benefits: [
        "Early access to innovations",
        "Fractional ownership model",
        "Liquid asset creation",
        "Impact-driven returns"
      ],
      cta: "Start Investing",
      ctaColor: "bg-gradient-to-r from-blue-600 to-cyan-600"
    },
    stakers: {
      title: "Expert Stakers",
      tagline: "Your expertise becomes earning power through trust. Fuel project growth by signaling trust and boosting reward emissions through their stake",
      icon: <Shield className="w-8 h-8" />,
      color: "from-orange-500 to-red-600",
      bgGradient: "from-orange-50 via-red-50 to-orange-50",
      shadowColor: "shadow-orange-500/20",
      problem: "Have expertise to identify good projects but no way to monetize it?",
      solution: "Stake on projects you trust and earn from being right",
      actions: [
        {
          step: "Review Projects",
          description: "Use your expertise to evaluate project potential",
          detail: "Analyze code quality, team, market potential, feasibility",
          icon: <Target className="w-5 h-5" />
        },
        {
          step: "Stake CEL",
          description: "Put your tokens where your conviction is",
          detail: "Signal trust in projects you believe will succeed",
          icon: <Shield className="w-5 h-5" />
        },
        {
          step: "Boost POI",
          description: "Your stake enhances project's POI emissions",
          detail: "Trusted projects get higher rewards, attracting more talent",
          icon: <Zap className="w-5 h-5" />
        },
        {
          step: "Earn Rewards",
          description: "Get staking rewards based on project success",
          detail: "Game-theoretically aligned to stake on good projects",
          icon: <Trophy className="w-5 h-5" />
        }
      ],
      benefits: [
        "Monetize your expertise",
        "Enhanced project rewards",
        "Risk-reward alignment",
        "Reputation building"
      ],
      cta: "Start Staking",
      ctaColor: "bg-gradient-to-r from-orange-600 to-red-600"
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep(prev => (prev + 1) % 4);
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 500);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const currentRole = roleData[activeRole];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 py-20">
        {/* Floating background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-purple-200/20 to-indigo-200/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-blue-200/20 to-cyan-200/20 rounded-full blur-3xl"></div>
        </div>

        {/* Header */}
        <div className="text-center mb-20 relative">
          <div className="inline-flex items-center gap-3 bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 text-white px-6 py-3 rounded-full text-sm font-bold mb-8 shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/30 transition-all duration-300">
            <Zap className="w-5 h-5 animate-pulse" />
            What can YOU do on CelystikHub?
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            How Trustless Open Innovation
            <span className="block bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent animate-pulse"> Works</span>
          </h2>
          
        </div>

        {/* Role Selector */}
        <div className="flex flex-wrap justify-center gap-6 mb-16">
          {Object.entries(roleData).map(([key, role]) => (
            <button
              key={key}
              onClick={() => setActiveRole(key)}
              className={`group flex items-center gap-4 px-8 py-4 rounded-3xl border-2 transition-all duration-500 transform hover:scale-105 ${
                activeRole === key
                  ? `bg-gradient-to-r ${role.color} text-white border-transparent shadow-2xl ${role.shadowColor} scale-105`
                  : 'bg-white/80 backdrop-blur-sm text-gray-700 border-gray-200 hover:border-gray-300 hover:shadow-xl hover:bg-white'
              }`}
            >
              <div className={`p-2 rounded-xl transition-all duration-300 ${
                activeRole === key ? 'bg-white/20' : 'bg-gray-100 group-hover:bg-gray-200'
              }`}>
                {role.icon}
              </div>
              <span className="font-bold text-lg">{role.title.split(' ')[1]}</span>
            </button>
          ))}
        </div>

        {/* Active Role Content */}
        <div className={`relative bg-gradient-to-br ${currentRole.bgGradient} backdrop-blur-sm rounded-3xl p-10 mb-16 border-2 border-white/50 shadow-2xl ${currentRole.shadowColor} overflow-hidden`}>
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-white/10 to-transparent rounded-full -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-white/10 to-transparent rounded-full translate-y-12 -translate-x-12"></div>
          
          <div className="relative grid lg:grid-cols-2 gap-16 items-start">
            {/* Left: Role Info */}
            <div className="space-y-8">
              <div className="flex items-start gap-6">
                <div className={`p-5 rounded-3xl bg-gradient-to-r ${currentRole.color} text-white shadow-2xl ${currentRole.shadowColor} transform rotate-3 hover:rotate-0 transition-transform duration-300`}>
                  {currentRole.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-black text-gray-900 mb-2">{currentRole.title}</h3>
                  <p className="text-gray-700 font-medium text-lg leading-relaxed">{currentRole.tagline}</p>
                </div>
              </div>

              {/* Problem/Solution */}
              <div className="space-y-6">
                <div className="group p-6 bg-gradient-to-r from-red-50 to-red-100 border-2 border-red-200 rounded-2xl hover:shadow-lg hover:shadow-red-500/10 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center text-white text-lg font-black mt-1 shadow-lg">!</div>
                    <div>
                      <p className="text-red-800 font-bold mb-2">Current Problem:</p>
                      <p className="text-red-700 text-lg leading-relaxed">{currentRole.problem}</p>
                    </div>
                  </div>
                </div>
                
                <div className="group p-6 bg-gradient-to-r from-green-50 to-emerald-100 border-2 border-green-200 rounded-2xl hover:shadow-lg hover:shadow-green-500/10 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-8 h-8 text-green-600 mt-1 group-hover:scale-110 transition-transform duration-300" />
                    <div>
                      <p className="text-green-800 font-bold mb-2">CelystikHub Solution:</p>
                      <p className="text-green-700 text-lg leading-relaxed">{currentRole.solution}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Benefits */}
              <div className="space-y-4">
                <h4 className="font-black text-gray-900 text-xl mb-4">Why This Works:</h4>
                <div className="grid gap-3">
                  {currentRole.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-xl hover:bg-white/80 transition-all duration-300 group">
                      <CheckCircle className="w-6 h-6 text-green-600 group-hover:scale-110 transition-transform duration-300" />
                      <span className="text-gray-800 font-medium text-lg">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <button 
                onClick={() => navigate('/projects')}
                className={`group w-full ${currentRole.ctaColor} text-white px-10 py-5 rounded-2xl font-bold text-xl hover:shadow-2xl ${currentRole.shadowColor} transition-all duration-500 flex items-center justify-center gap-4 transform hover:scale-105`}
              >
                <span>{currentRole.cta}</span>
                <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform duration-300" />
              </button>
            </div>

            {/* Right: Action Steps */}
            <div className="space-y-6">
              <h4 className="text-2xl font-black text-gray-900 mb-8 text-center">How It Works:</h4>
              <div className="space-y-6">
                {currentRole.actions.map((action, index) => (
                  <div
                    key={index}
                    className={`group relative p-8 bg-white/80 backdrop-blur-sm rounded-3xl border-2 transition-all duration-700 hover:shadow-xl ${
                      currentStep === index
                        ? 'border-indigo-400 shadow-2xl shadow-indigo-500/20 bg-white scale-105 transform'
                        : 'border-gray-200 hover:border-gray-300'
                    } ${isAnimating && currentStep === index ? 'animate-pulse' : ''}`}
                  >
                    {/* Step connector line */}
                    {index < currentRole.actions.length - 1 && (
                      <div className="absolute -bottom-3 left-1/2 w-1 h-6 bg-gradient-to-b from-gray-300 to-transparent transform -translate-x-0.5"></div>
                    )}
                    
                    <div className="flex items-start gap-6">
                      <div className={`relative p-4 rounded-2xl transition-all duration-500 ${
                        currentStep === index 
                          ? `bg-gradient-to-r ${currentRole.color} text-white shadow-lg transform rotate-6 hover:rotate-0` 
                          : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                      }`}>
                        {action.icon}
                        {/* Glowing effect for active step */}
                        {currentStep === index && (
                          <div className={`absolute inset-0 bg-gradient-to-r ${currentRole.color} rounded-2xl blur opacity-30 -z-10`}></div>
                        )}
                      </div>
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-4 mb-3">
                          <span className={`text-sm font-black px-3 py-1 rounded-full transition-all duration-300 ${
                            currentStep === index
                              ? `bg-gradient-to-r ${currentRole.color} text-white shadow-lg`
                              : 'bg-gray-200 text-gray-600 group-hover:bg-gray-300'
                          }`}>
                            Step {index + 1}
                          </span>
                          <h5 className="font-black text-gray-900 text-lg">{action.step}</h5>
                        </div>
                        <p className="text-gray-700 text-lg font-medium leading-relaxed">{action.description}</p>
                        <p className="text-gray-500 italic leading-relaxed bg-gray-50 p-3 rounded-xl border-l-4 border-gray-300">{action.detail}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA Section, let's add a button to navigate to the projects page */}
        <div className="relative text-center bg-gradient-to-r from-gray-900 via-indigo-900 to-gray-900 text-white rounded-3xl p-16 shadow-2xl shadow-gray-900/50 overflow-hidden">
          {/* Decorative background */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-indigo-500/10 to-transparent"></div>
          <div className="absolute top-0 left-1/4 w-2 h-2 bg-white rounded-full animate-ping"></div>
          <div className="absolute top-1/4 right-1/4 w-1 h-1 bg-indigo-300 rounded-full animate-pulse"></div>
          <div className="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 bg-purple-300 rounded-full animate-bounce"></div>
          
          <div className="relative">
            <h3 className="text-4xl md:text-5xl font-bold mb-6">Ready to Start Innovating?</h3>
            <p className="text-gray-300 mb-12 max-w-3xl mx-auto text-xl leading-relaxed">
              Join the decentralized innovation ecosystem where your work creates long-term value for everyone involved.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              {Object.entries(roleData).map(([key, role]) => (
                <button
                  key={key}
                  onClick={() => navigate('/projects')}
                  className={`group ${role.ctaColor} text-white px-8 py-4 rounded-2xl font-bold text-lg hover:shadow-2xl ${role.shadowColor} transition-all duration-500 flex items-center gap-3 transform hover:scale-110`}
                >
                  <div className="p-2 bg-white/20 rounded-lg group-hover:bg-white/30 transition-colors duration-300">
                    {role.icon}
                  </div>
                  <span>{role.cta}</span>
                
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CelystikActionGuide;